import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { CONVERSATION_FIELDS, VIEW_PRESETS, isLiveEntry, matchesSegment, type ConditionEntry, type Subject } from '@shared/segments'
import { dayKeyOf, dayLabelOf, shouldShowSender, timeOnly } from '@shared/messageGrouping'
import { humaniseMinutes, minutesUntil } from '@shared/duration'
import { insertMention, matchMentionCandidates, mentionQuery as computeMentionQuery } from '@shared/mentions'
import { formatBytes } from '@shared/media'
import { detectDelimiter, parseCsv } from '@shared/csv'
import {
  api,
  ApiError,
  mediaUrl,
  type ConversationMessage,
  type InboxConversation,
  type InboxTicket,
  type MessageAction,
  type ProductView,
  type RewriteStyle,
  type TeamMemberView,
  type TicketEvent,
  type UploadedMedia,
} from '../lib/api'
import { PropertiesPanel } from './PropertiesPanel'
import { SegmentBuilder } from './SegmentBuilder'
import { DatePresetChips, TaskForm } from './Tasks'
import { describeRecurrence, EMPTY_RECURRENCE, RecurrenceFields, recurrencePayload, type RecurrenceState } from './RecurrenceFields'

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏']

/**
 * WhatsApp's ticks, in the words WhatsApp itself uses for them.
 *
 * Two ticks and blue two ticks are the same glyph in this product — colour is the only thing
 * that separates them in WhatsApp too — so the title carries the meaning for anyone who reads
 * the thread with a screen reader or cannot tell the two apart.
 */
const ACK_MARK: Record<NonNullable<ConversationMessage['ack']>, { mark: string; title: string }> = {
  sent: { mark: '✓', title: 'Sent' },
  delivered: { mark: '✓✓', title: 'Delivered to their phone' },
  read: { mark: '✓✓', title: 'Read' },
  failed: { mark: '!', title: 'WhatsApp could not deliver this' },
}

/**
 * The ticks on one outbound message.
 *
 * Nothing at all when there is no ack: a message sent before this existed, or one sent while
 * the number was disconnected, has none, and a grey "sent" invented for it would be a claim
 * about WhatsApp that nothing made.
 */
function AckMark({ ack }: { ack: ConversationMessage['ack'] }) {
  if (!ack) return null
  const { mark, title } = ACK_MARK[ack]
  return (
    <span
      title={title}
      style={{
        marginLeft: '6px',
        color: ack === 'read' ? 'var(--md-primary)' : ack === 'failed' ? 'var(--md-error)' : undefined,
      }}
    >
      {mark}
    </span>
  )
}

type TimelineEntry =
  | { kind: 'message'; at: string; message: ConversationMessage }
  | { kind: 'event'; at: string; event: TicketEvent }

/**
 * One timeline, not two: a message and a ticket event sort together by when they actually
 * happened, the way a real WhatsApp thread interleaves "X added Y" with the messages around it
 * rather than keeping a separate log a teammate has to go find.
 */
function mergeTimelineEntries(messages: ConversationMessage[], events: TicketEvent[]): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    ...messages.map((message): TimelineEntry => ({ kind: 'message', at: message.sentAt, message })),
    ...events.map((event): TimelineEntry => ({ kind: 'event', at: event.createdAt, event })),
  ]
  return entries.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
}

/**
 * Centered gray system text, WhatsApp's own convention for "call ended" / "X added Y" — used
 * here for ticket assignment, resolution and reply-latency events. `detail` is already a
 * complete sentence written where the event was recorded, not reconstructed from `type` here.
 */
/**
 * Keeps the conversation pinned to the newest message, the way every chat client does.
 *
 * The list had no scroll management at all: opening a thread left it at scrollTop 0, so an agent
 * landed on the *oldest* message and had to scroll down to find out what the customer actually
 * just said. On a thread with any history that is the wrong end of the conversation every single
 * time.
 *
 * Sticking is conditional on purpose. Snapping to the bottom while someone is reading back
 * through history — because a new message happened to arrive — would yank the page out from
 * under them. So it follows only when they were already at the bottom, and otherwise offers the
 * jump button below.
 */
function useStickToNewest(
  /** Changing this means a different conversation: jump, do not animate. */
  threadKey: string | null,
  /** Changing this means new content in the same conversation. */
  messageCount: number,
) {
  // A callback ref held in state, not a RefObject. Selecting a thread flips `threadKey` while
  // the query is still loading, so the list is not mounted yet — an effect keyed on a stable
  // ref object runs against a null node and never re-runs when the element finally appears.
  // Storing the node in state means every consumer below re-runs the moment it mounts.
  const [el, setEl] = useState<HTMLDivElement | null>(null)
  const [atBottom, setAtBottom] = useState(true)
  const [missedWhileAway, setMissedWhileAway] = useState(false)
  const lastCount = useRef(messageCount)

  const scrollToBottom = (preferSmooth = false) => {
    if (!el) return
    // Smooth only when the platform will actually animate it and the reader has not asked for
    // less motion. Passing 'smooth' unconditionally is a silent no-op wherever the compositor
    // will not run it, which turns the jump button into a dead control rather than a jerky one.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    el.scrollTo({ top: el.scrollHeight, behavior: preferSmooth && !reduced ? 'smooth' : 'auto' })
    // Belt and braces: if the smooth request is ignored entirely, land there anyway.
    el.scrollTop = el.scrollHeight
    setMissedWhileAway(false)
  }

  // A different thread always opens at the newest message. Layout effect so it happens before
  // paint — scrolling after paint shows the top of the thread for a frame first.
  useLayoutEffect(() => {
    if (!el) return
    el.scrollTop = el.scrollHeight
    setAtBottom(true)
    setMissedWhileAway(false)
    lastCount.current = messageCount
    // messageCount deliberately omitted: this is the thread-switch case only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadKey, el])

  // New content in the thread already open.
  useLayoutEffect(() => {
    if (messageCount === lastCount.current) return
    const grew = messageCount > lastCount.current
    lastCount.current = messageCount
    if (!grew || !el) return
    if (atBottom) el.scrollTop = el.scrollHeight
    else setMissedWhileAway(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageCount, el])

  useEffect(() => {
    if (!el) return
    const onScroll = () => {
      // A couple of pixels of slack: fractional scroll heights mean an exact equality test
      // reads as "not quite at the bottom" on perfectly ordinary scroll positions.
      const bottom = el.scrollTop >= el.scrollHeight - el.clientHeight - 4
      setAtBottom(bottom)
      if (bottom) setMissedWhileAway(false)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [el])

  return { listRef: setEl, atBottom, missedWhileAway, scrollToBottom }
}

/** The "Today" / "Yesterday" rule between days, so the date can come off every message. */
function DaySeparator({ iso }: { iso: string }) {
  return (
    <div className="cad-day-sep" role="separator" aria-label={dayLabelOf(iso)}>
      <span>{dayLabelOf(iso)}</span>
    </div>
  )
}

function SystemNotice({ event, members }: { event: TicketEvent; members: TeamMemberView[] }) {
  const actor = event.actorUserId ? members.find((m) => m.userId === event.actorUserId)?.name : null
  return (
    <div className="cad-system-notice">
      <span>
        {actor ? `${actor}: ` : ''}
        {event.detail ?? event.type}
      </span>
    </div>
  )
}

/**
 * Whether the customer is at their phone, above the thread.
 *
 * Renders nothing at all unless there is something true to show — presence is unknown far
 * more often than it is known (see `@shared/chatPresence`), and an indicator that is usually
 * wrong is worse than no indicator. Polled on its own short interval rather than with the
 * thread: the first request only subscribes, so an answer can only arrive on a later one.
 */
function PresenceIndicator({ conversationId }: { conversationId: string }) {
  const presence = useQuery({
    queryKey: ['conversation-presence', conversationId],
    queryFn: () => api.conversationPresence(conversationId),
    refetchInterval: 10_000,
    retry: false,
  })

  const reading = presence.data?.presence
  if (!reading?.label) return null

  return (
    <span className="cad-pill" data-tone={reading.state === 'unknown' ? undefined : 'accent'}>
      {reading.label}
    </span>
  )
}

const MEDIA_LABEL: Record<NonNullable<ConversationMessage['mediaType']>, string> = {
  image: 'Photo',
  video: 'Video',
  audio: 'Voice note',
  document: 'File',
  sticker: 'Sticker',
  location: 'Location',
  contact: 'Contact card',
}

/**
 * What arrived alongside the words, when anything did.
 *
 * Split on whether the bytes are actually held. An outbound attachment was uploaded through
 * this app and can be shown; an inbound one is currently only ever *labelled* — the session
 * host records a media type and nothing downloads the file, which HANDOVER §8 names as the
 * open gap. Rendering an honest "Photo" chip is better than an `<img>` pointed at a key that
 * was never written.
 */
function MessageAttachment({ message }: { message: ConversationMessage }) {
  if (!message.mediaType) return null
  const chip = { marginTop: '4px', display: 'inline-block' } as const

  if (message.mediaType === 'location') {
    // The pin's coordinates live in the body as a maps link, on both ends — see
    // `locationBody` in @shared/media for why they are not a column.
    const link = message.body?.match(/https?:\/\/\S+/)?.[0]
    if (!link) return <span className="cad-pill" style={chip}>Location</span>
    return (
      <a className="cad-pill" href={link} target="_blank" rel="noreferrer" style={chip}>
        Open the map
      </a>
    )
  }

  if (!message.mediaUrl) {
    return (
      <span className="cad-pill" style={chip} title="Received attachments are not downloaded yet.">
        {MEDIA_LABEL[message.mediaType]}
      </span>
    )
  }

  const href = mediaUrl(message.mediaUrl)
  if (message.mediaType === 'image') {
    return (
      <a href={href} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: '6px' }}>
        <img
          src={href}
          alt={message.body ?? 'Attachment'}
          style={{ maxWidth: '240px', maxHeight: '240px', borderRadius: '8px', objectFit: 'cover' }}
        />
      </a>
    )
  }

  return (
    <a className="cad-pill" href={href} target="_blank" rel="noreferrer" style={chip}>
      {MEDIA_LABEL[message.mediaType]}
    </a>
  )
}


const shortTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'


/**
 * A stable hue for a label dot, derived from its name rather than fetched from the labels
 * table — the thread-row list only carries label names today (`InboxConversation.labels:
 * string[]`), not colors, and this row is dense enough already without a second query just to
 * paint dots. Two labels with the same name always land on the same hue across renders and
 * across rows, which is what makes a glance at the dot cluster useful at all.
 */
function labelHue(name: string): number {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return Math.abs(hash) % 360
}

/**
 * What to call a chat on screen.
 *
 * A group has a subject; a 1:1 chat has the contact's name. The raw WhatsApp id is the last
 * resort, not the default — `9190000000@s.whatsapp.net` is not something anyone recognises as
 * a customer, and it was what every direct thread showed before this existed. The number is
 * stripped off the id so even that fallback reads as a phone number rather than a routing
 * address.
 */
export function conversationTitle(
  conversation: Pick<InboxConversation, 'subject' | 'contactName' | 'remoteId'>,
): string {
  return conversation.subject ?? conversation.contactName ?? conversation.remoteId.split('@')[0]
}


function SlaPill({ ticket }: { ticket: InboxTicket }) {
  if (ticket.firstResponseAt) return <span className="cad-pill" data-tone="good">Answered</span>
  const left = minutesUntil(ticket.dueAt)
  if (left === null) return null
  if (left < 0) {
    return (
      <span className="cad-pill" data-tone="bad" title={`Reply target passed ${Math.abs(left)} minutes ago`}>
        {humaniseMinutes(Math.abs(left))} overdue
      </span>
    )
  }
  if (left <= 15) return <span className="cad-pill" data-tone="warn">{left}m left</span>
  return <span className="cad-pill" title={`${left} minutes until the reply target`}>{humaniseMinutes(left)} left</span>
}

/**
 * How long it has been since the customer last wrote — not a WhatsApp session-window
 * countdown. That mechanism (a 24-hour reply window past which only a pre-approved template
 * may be sent) is a WhatsApp Business Platform / Cloud API rule, enforced by Meta at the
 * official API's edge. Wami deliberately doesn't connect that way (see /trust) — it pairs
 * like WhatsApp Web, which has no such window and no template system to fall back on. Showing
 * a countdown implying one would be telling an agent something false about this transport.
 * This is the honest version of the same underlying need: a plain freshness signal, ticking
 * live so it doesn't go stale itself while a thread sits open.
 */
function LastCustomerMessagePill({ messages }: { messages: ConversationMessage[] }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(timer)
  }, [])

  const lastInbound = [...messages].reverse().find((m) => m.direction === 'inbound' && !m.isPrivateNote)
  if (!lastInbound) return null

  const hours = (now - new Date(lastInbound.sentAt).getTime()) / 3_600_000
  // The clock glyph beside it already says "elapsed time", so the word "Wrote" was carrying no
  // information and 55px of a header that could not fit one row because of it. The full
  // sentence still lives in the tooltip below.
  // "ago" earns its four characters: this pill can sit next to an SLA pill reading "8h overdue",
  // and two bare durations side by side are indistinguishable at a glance.
  const label =
    hours < 1 ? '< 1h ago' : hours < 24 ? `${Math.floor(hours)}h ago` : `${Math.floor(hours / 24)}d ago`
  const tone = hours >= 24 ? 'warn' : undefined

  return (
    <span
      className="cad-pill row gap-1"
      data-tone={tone}
      style={{ alignItems: 'center', display: 'inline-flex' }}
      title="Time since the customer's last message. Wami has no WhatsApp Business API session window or templates — this is a freshness signal, not a hard cutoff."
      // The visible text is a bare duration now; a screen reader gets the sentence.
      aria-label={`Customer last wrote ${label}`}
    >
      <ClockIcon />
      {label}
    </span>
  )
}

/** What a saved view filters against — a conversation, folded together with its open ticket. */
function conversationSubject(conversation: InboxConversation, openTickets: InboxTicket[]): Subject {
  const ticket = openTickets.find((t) => t.conversationId === conversation.id)
  return {
    kind: conversation.kind,
    labels: conversation.labels ?? [],
    status: ticket ? ticket.status : 'none',
    priority: ticket?.priority ?? null,
    category: ticket?.category ?? null,
    assignee: ticket?.assigneeUserId ?? conversation.assignedTo ?? null,
    unread: conversation.unreadCount,
    breached: ticket?.breached ?? false,
    // Unread resets the moment a reply is recorded, so it doubles as "still owed a reply".
    awaitingReply: conversation.unreadCount > 0,
    lastMessageAt: conversation.lastMessageAt,
    channelNumber: conversation.channelNumber,
  }
}

/**
 * A small, honest disclosure of who besides the viewer can read this chat — computed from
 * the exact label/number grants that gate visibility, not a separate claim that could drift
 * from the truth. Lazy: only fetched once a thread is actually open, not for every row in
 * the list.
 */
/**
 * Who can see this chat, and why — a click-open popover rather than a hover tooltip, so the
 * reasoning (which labels grant it) is actually reachable, not just the headcount. Wami's access
 * model is label-based rather than per-chat grants, so this explains the labels doing the work
 * instead of pretending a per-conversation override exists where none does.
 */
function VisibilityIndicator({ conversationId, labels }: { conversationId: string; labels: string[] }) {
  const [open, setOpen] = useState(false)
  const visibility = useQuery({
    queryKey: ['conversation-visibility', conversationId],
    queryFn: () => api.conversationVisibility(conversationId),
    enabled: open,
    retry: false,
  })

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="cad-icon-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={
          visibility.data
            ? `${visibility.data.count} ${visibility.data.count === 1 ? 'person can' : 'people can'} see this`
            : 'Who can see this?'
        }
        aria-label={
          visibility.data
            ? `${visibility.data.count} ${visibility.data.count === 1 ? 'person can' : 'people can'} see this`
            : 'Who can see this?'
        }
      >
        <EyeIcon />
      </button>
      {open && (
        <div className="cad-card cad-popover" style={{ width: '260px' }}>
          <div className="stack gap-2">
            {labels.length > 0 ? (
              <>
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>
                  Access is granted by label — anyone with one of these can see this chat:
                </span>
                <div className="row gap-1 wrap">
                  {labels.map((name) => (
                    <span key={name} className="cad-pill">{name}</span>
                  ))}
                </div>
              </>
            ) : (
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>
                No labels on this chat — only unrestricted roles (owner/admin) can see it.
              </span>
            )}
            {visibility.isLoading ? (
              <div style={{ height: '20px' }} />
            ) : (
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {(visibility.data?.members ?? []).map((m) => (
                  <li key={m.userId} style={{ fontSize: 'var(--cad-text-md)' }}>{m.name}</li>
                ))}
              </ul>
            )}
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>
              Change this under Team → a person's Access.
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * "Summarise this conversation" — a click-open popover in the same shape as
 * `VisibilityIndicator`, but backed by a mutation rather than a cached query: a summary goes
 * stale the moment a new message arrives, so nothing here is cached across opens, and a second
 * click always asks again rather than showing what may already be an outdated answer.
 */
function AiSummaryButton({ conversationId }: { conversationId: string }) {
  const [open, setOpen] = useState(false)
  const summarize = useMutation({ mutationFn: () => api.summarizeConversation(conversationId) })

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="cad-icon-btn"
        onClick={() => {
          const next = !open
          setOpen(next)
          if (next) summarize.mutate()
        }}
        aria-expanded={open}
        title="Summarise this conversation"
        aria-label="Summarise this conversation"
      >
        <SummariseIcon />
      </button>
      {open && (
        <div className="cad-card cad-popover" style={{ width: '320px' }}>
          <div className="stack gap-2">
            {summarize.isPending && <span className="cad-muted">Reading the conversation…</span>}
            {summarize.data && <p style={{ margin: 0, fontSize: 'var(--cad-text-md)' }}>{summarize.data.summary}</p>}
            {summarize.isError && (
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
                {summarize.error instanceof ApiError || summarize.error instanceof Error
                  ? summarize.error.message
                  : 'That could not be summarised.'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * "Draft reply" — the same knowledge-base retrieval `Knowledge.tsx`'s "Try a question" tester
 * already calls, aimed at the customer's own last message instead of a hand-typed test query.
 * The backend has done this lookup since the knowledge feature shipped; nothing in the inbox
 * ever asked it a real question until now. Retrieval, not generation: this surfaces the closest
 * matching knowledge-base entry for the agent to read and send, the same as the Settings tester,
 * not an LLM writing new prose — `usedModel` only reports whether semantic search took part in
 * finding it.
 */
function AiDraftReplyButton({
  conversationId,
  question,
  onUseDraft,
}: {
  conversationId: string
  question: string | null
  onUseDraft: (body: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ask = useMutation({
    mutationFn: (q: string) => api.askKnowledge(q, true, conversationId),
  })

  if (!question) return null

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="cad-icon-btn"
        onClick={() => {
          const next = !open
          setOpen(next)
          if (next) ask.mutate(question)
        }}
        aria-expanded={open}
        title="Draft a reply from your knowledge base"
        aria-label="Draft a reply from your knowledge base"
      >
        <DraftIcon />
      </button>
      {open && (
        <div className="cad-card cad-popover" style={{ width: '340px' }}>
          <div className="stack gap-2">
            {ask.isPending && <span className="cad-muted">Checking the knowledge base…</span>}
            {ask.isError && (
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
                {ask.error instanceof ApiError || ask.error instanceof Error
                  ? ask.error.message
                  : 'Could not search the knowledge base.'}
              </span>
            )}
            {ask.data && ask.data.hits.length === 0 && (
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
                Nothing in the knowledge base is close enough to suggest.
              </span>
            )}
            {ask.data?.hits.slice(0, 2).map((hit) => (
              <div key={hit.id} className="stack gap-1" style={{ borderTop: '1px solid var(--cad-border)', paddingTop: '8px' }}>
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>{hit.title}</span>
                <p style={{ margin: 0, fontSize: 'var(--cad-text-md)' }}>{hit.body}</p>
                <div className="row">
                  <button
                    type="button"
                    className="cad-btn"
                    data-variant="text"
                    onClick={() => {
                      onUseDraft(hit.body)
                      setOpen(false)
                    }}
                  >
                    Use this reply
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Small line icons for the message toolbar and composer — hand-drawn rather than a dependency,
 * since the whole set is five simple shapes and the product has no icon library today.
 */
function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2 12l.7-3 6.8-6.8 2.3 2.3L5 11.3 2 12z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}
function ReactIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="5" cy="6" r="0.8" fill="currentColor" />
      <circle cx="9" cy="6" r="0.8" fill="currentColor" />
      <path d="M4.5 8.5c.7 1 1.6 1.5 2.5 1.5s1.8-.5 2.5-1.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}
function ForwardIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2 7h9M7.5 3l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function PinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M7 1.5c-2 0-3.5 1.5-3.5 3.5 0 2.5 3.5 7 3.5 7s3.5-4.5 3.5-7c0-2-1.5-3.5-3.5-3.5z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <circle cx="7" cy="5" r="1.2" fill="currentColor" />
    </svg>
  )
}
function MoreIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="3" cy="7" r="1.3" fill="currentColor" />
      <circle cx="7" cy="7" r="1.3" fill="currentColor" />
      <circle cx="11" cy="7" r="1.3" fill="currentColor" />
    </svg>
  )
}
function AttachIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M9.5 3.2L5 7.7a1.8 1.8 0 002.5 2.5l4-4a2.8 2.8 0 00-4-4L3.3 6.4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4v3.2l2.2 1.3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
/* The three thread-header actions. Word-labelled pills, they were ~280px of a 415px column and
   forced the header to wrap onto three rows; every row cost the conversation below it. As icons
   they sit on one. Each keeps its full sentence in `title` and `aria-label`, so nothing is lost
   to anyone hovering or using a screen reader. */
function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M1 7s2.2-3.6 6-3.6S13 7 13 7s-2.2 3.6-6 3.6S1 7 1 7Z" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
      <circle cx="7" cy="7" r="1.7" fill="none" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  )
}
function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="6.2" cy="6.2" r="3.9" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M9.2 9.2 12 12" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}
function SummariseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2.5 3.5h9M2.5 6.2h9M2.5 8.9h6M2.5 11.6h3.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}
function DraftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M9.4 2.4a1.3 1.3 0 0 1 1.9 1.9l-5.6 5.6-2.5.6.6-2.5Z" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M2 12.4h10" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  )
}
function TranslateIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2 3h5M4.5 2v1.5M2.8 3.5c.3 2.2 1.7 3.6 3.7 4.4M6 3.5c-.5 1.8-1.6 3.2-3 4.2" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 12l2.2-5.5L12.4 12M8.7 10.2h3" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
const VIEW_ICONS = ['📌', '⭐', '🔥', '⏰', '🚩', '✅', '👥', '💬']

const REWRITE_STYLE_LABEL: Record<RewriteStyle, string> = {
  professional: 'Sound professional',
  friendly: 'Sound friendlier',
  concise: 'Make it shorter',
  proofread: 'Fix spelling & grammar',
}

function RewriteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2 12l.7-2.6 6.4-6.4a1.2 1.2 0 011.7 0l.9.9a1.2 1.2 0 010 1.7L5.3 12l-2.6.7z" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" strokeLinecap="round" />
      <path d="M8 3.5L10.5 6" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  )
}
function ProductIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2 4.5L7 2l5 2.5v5L7 12 2 9.5v-5z" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M2 4.5L7 7l5-2.5M7 7v5" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
    </svg>
  )
}
function AllChatsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true">
      <path
        d="M2 4.5A1.5 1.5 0 013.5 3h7A1.5 1.5 0 0112 4.5v5A1.5 1.5 0 0110.5 11H5l-2.3 1.8a.4.4 0 01-.7-.3V4.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  )
}
function NeedsReplyIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M8.5 3.5L4 7l4.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 7h6" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}
function BreachingIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4.3v3.2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="7" cy="9.5" r="0.7" fill="currentColor" />
    </svg>
  )
}
function ArchivedIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true">
      <rect x="2" y="2.5" width="10" height="2.4" rx="0.6" fill="none" stroke="currentColor" strokeWidth="1.1" />
      <path d="M2.8 4.9v5.6a1 1 0 001 1h6.4a1 1 0 001-1V4.9" fill="none" stroke="currentColor" strokeWidth="1.1" />
      <path d="M5.5 7.5h3" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  )
}

/**
 * One icon-only tab in the inbox switcher, with a small numeric badge instead of a text count —
 * the density-audit fix for filter labels that spelled out "Needs reply · 4" in full words,
 * three to four times wider than the icon itself needs to be.
 */
function SwitcherTab({
  label,
  active,
  tone,
  count,
  onClick,
  children,
}: {
  label: string
  active: boolean
  tone?: 'bad'
  count?: number
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      className="cad-switcher-tab"
      data-active={active || undefined}
      data-tone={tone}
      role="tab"
      aria-selected={active}
      title={typeof count === 'number' ? `${label} · ${count}` : label}
      aria-label={typeof count === 'number' ? `${label}, ${count}` : label}
      onClick={onClick}
    >
      {children}
      {Boolean(count) && <span className="cad-switcher-badge">{count! > 99 ? '99+' : count}</span>}
    </button>
  )
}

/** A round, icon-only action button — the toolbar unit that replaced a row of same-weight text
 *  links. `label` is the stable accessible name; `title` may override it to explain a disabled
 *  state without changing what the button is called. */
function IconButton({
  label,
  title,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string
  title?: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      className="cad-icon-btn"
      data-tone={danger ? 'bad' : undefined}
      title={title ?? label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

/**
 * A menu anchored to a trigger button, rendered through a portal to `document.body` rather than
 * as a child of it.
 *
 * The message list scrolls its own container (`.message-list`), and every message bubble lives
 * inside it — an ordinary `position: absolute` popover there gets clipped the instant it would
 * draw past the container's edge, which for a menu opening near the bottom of a bubble that is
 * itself near the bottom of the scroll viewport is nearly always. Fixed positioning computed
 * from the trigger's own bounding rect sidesteps that entirely. Position is captured once, on
 * open, the same way the app's other popovers never track their anchor either — every item in
 * this menu already closes it on click, which is the case that actually matters.
 */
function AnchoredMenu({
  anchorRef,
  open,
  children,
}: {
  anchorRef: RefObject<HTMLButtonElement | null>
  open: boolean
  children: ReactNode
}) {
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null)

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setPos(null)
      return
    }
    const rect = anchorRef.current.getBoundingClientRect()
    setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
  }, [open, anchorRef])

  if (!open || !pos) return null

  return createPortal(
    <div
      className="cad-portal-root cad-card cad-popover cad-menu"
      role="menu"
      style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 50 }}
    >
      {children}
    </div>,
    document.body,
  )
}

/**
 * One message, with the actions WhatsApp itself supports layered on top.
 *
 * None of edit/delete/pin/react have ever run against a real WhatsApp account — see
 * HANDOVER.md §8 — so this is verified as far as the request leaving the browser and the
 * session host's response updating the row; whether WhatsApp accepts the call has not been.
 */
function MessageBubble({
  message,
  conversationId,
  conversations,
  tickets,
  members,
  onForwarded,
  showSender = true,
  searchState = null,
}: {
  message: ConversationMessage
  conversationId: string
  conversations: InboxConversation[]
  tickets: InboxTicket[]
  members: TeamMemberView[]
  onForwarded: () => void
  /** False for a message continuing a run from the same sender — the name line is dropped and
   *  only the time remains, the way a chat client collapses consecutive turns. */
  showSender?: boolean
  /** 'hit' for any search match, 'active' for the one currently stepped to. */
  searchState?: 'hit' | 'active' | null
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(message.body ?? '')
  const [reacting, setReacting] = useState(false)
  const [forwarding, setForwarding] = useState(false)
  const [forwardTo, setForwardTo] = useState('')
  const [attaching, setAttaching] = useState(false)
  const [attachTo, setAttachTo] = useState('')
  const [flagging, setFlagging] = useState(false)
  const [flagReason, setFlagReason] = useState('')
  const [creatingTicket, setCreatingTicket] = useState(false)
  const [ticketTitle, setTicketTitle] = useState('')
  const [ticketPriority, setTicketPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal')
  const [ticketAssignee, setTicketAssignee] = useState('')
  const [ticketDueAt, setTicketDueAt] = useState('')
  const [creatingTask, setCreatingTask] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const [translated, setTranslated] = useState<string | null>(null)

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] })

  const translate = useMutation({
    mutationFn: () => api.translateMessage(conversationId, message.body ?? ''),
    onSuccess: (result) => setTranslated(result.translated),
  })

  const act = useMutation({
    mutationFn: (body: {
      action: MessageAction
      text?: string
      emoji?: string
      reason?: string
      ticketId?: string
      title?: string
      category?: string
      priority?: 'low' | 'normal' | 'high' | 'urgent'
      assigneeUserId?: string
      dueAt?: string
    }) => api.messageAction(conversationId, message.id, body),
    onSuccess: () => {
      setEditing(false)
      setReacting(false)
      setAttaching(false)
      setAttachTo('')
      setFlagging(false)
      setFlagReason('')
      setCreatingTicket(false)
      setTicketTitle('')
      setTicketPriority('normal')
      setTicketAssignee('')
      setTicketDueAt('')
      void refresh()
    },
  })

  const forward = useMutation({
    mutationFn: (toConversationId: string) => api.forwardMessage(conversationId, message.id, toConversationId),
    onSuccess: () => {
      setForwarding(false)
      setForwardTo('')
      onForwarded()
    },
  })

  const error = act.error ?? forward.error
  const errorMessage = error instanceof ApiError || error instanceof Error ? error.message : undefined

  // The server already decides whether to hand over the original content for a deleted
  // message (the workspace's "reveal deleted messages" setting, only relevant to a masked
  // viewer — see lib/masking.ts) — a null body here means it was withheld, not that there
  // never was one, so the tombstone is keyed off that rather than off deletedFor alone.
  if (message.deletedFor === 'everyone' && !message.body) {
    return (
      <div className={`cad-bubble cad-bubble-${message.direction}`} data-note={message.isPrivateNote}>
        <p className="cad-muted" style={{ margin: 0, fontStyle: 'italic' }}>This message was deleted.</p>
      </div>
    )
  }

  const canActOnWhatsApp = Boolean(message.externalId) && !message.isPrivateNote

  return (
    <div
      className={`cad-bubble cad-bubble-${message.direction}`}
      data-note={message.isPrivateNote}
      data-message-id={message.id}
      data-search={searchState ?? undefined}
    >
      <div className="row between gap-3">
        {showSender ? (
          <span style={{ fontWeight: 500, fontSize: 'var(--cad-text-md)' }}>
            {message.direction === 'inbound'
              ? (message.senderName ?? message.senderPhone ?? 'Customer')
              : message.isPrivateNote
                ? 'Private note'
                : 'You'}
          </span>
        ) : (
          <span />
        )}
        <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>
          {timeOnly(message.sentAt)}
          {message.editedAt ? ' · edited' : ''}
          {message.deletedFor === 'everyone' ? ' · deleted, shown by workspace setting' : ''}
          {message.direction === 'outbound' && !message.isPrivateNote && <AckMark ack={message.ack} />}
        </span>
      </div>

      {editing ? (
        <div className="stack gap-2" style={{ marginTop: '6px' }}>
          <input className="cad-input" value={editText} onChange={(e) => setEditText(e.target.value)} />
          <div className="row gap-2">
            <button
              type="button"
              className="cad-btn"
              data-variant="text"
              disabled={!editText.trim() || act.isPending}
              onClick={() => act.mutate({ action: 'edit', text: editText.trim() })}
            >
              Save
            </button>
            <button type="button" className="cad-btn" data-variant="text" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p style={{ margin: '4px 0 0' }}>{translated ?? message.body}</p>
      )}

      {message.direction === 'inbound' && message.body && !editing && (
        <button
          type="button"
          className="cad-btn"
          data-variant="text"
          style={{ fontSize: 'var(--cad-text-xs)', padding: 0, marginTop: '2px', height: 'auto' }}
          disabled={translate.isPending}
          onClick={() => (translated ? setTranslated(null) : translate.mutate())}
        >
          {translate.isPending ? 'Translating…' : translated ? 'View original' : 'Translate'}
        </button>
      )}

      <MessageAttachment message={message} />

      {message.reactions.length > 0 && (
        <div className="row gap-1" style={{ marginTop: '4px' }}>
          {message.reactions.map((emoji) => (
            <span key={emoji} className="cad-pill">{emoji}</span>
          ))}
        </div>
      )}
      {message.pinnedAt && (
        <span className="cad-pill" data-tone="accent" style={{ marginTop: '4px' }}>Pinned</span>
      )}
      {message.flaggedAt && (
        <span className="cad-pill" data-tone="warn" style={{ marginTop: '4px' }} title={message.flagReason ?? undefined}>
          Flagged{message.flagReason ? `: ${message.flagReason}` : ''}
        </span>
      )}
      {message.markedInternal && (
        <span className="cad-pill" data-tone="accent" style={{ marginTop: '4px' }}>
          Internal only
        </span>
      )}
      {message.ticketId && (
        <span className="cad-pill" style={{ marginTop: '4px' }}>
          {tickets.find((t) => t.id === message.ticketId)?.reference ?? 'Attached to a ticket'}
        </span>
      )}

      {!message.isPrivateNote && !editing && (
        // Hidden until the bubble is hovered or one of its own buttons has focus — a reading
        // pane with every action visible on every message reads as noisy well before the list
        // gets long; the actions are still one hover or Tab away, never removed.
        //
        // Only the handful of actions someone reaches for on nearly every message get their own
        // icon here — everything rarer, plus both delete actions, lives behind "More" so a
        // customer-facing "React" and a destructive "Delete for everyone" never compete for the
        // same visual weight.
        <div className="row gap-1 wrap cad-bubble-actions" style={{ marginTop: '6px' }}>
          {message.direction === 'outbound' && (
            <IconButton
              label="Edit"
              title={canActOnWhatsApp ? 'Edit' : 'This message has no WhatsApp id on record.'}
              disabled={!canActOnWhatsApp}
              onClick={() => {
                setEditText(message.body ?? '')
                setEditing(true)
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          <IconButton
            label="React"
            title={canActOnWhatsApp ? 'React' : 'This message has no WhatsApp id on record.'}
            disabled={!canActOnWhatsApp}
            onClick={() => setReacting((v) => !v)}
          >
            <ReactIcon />
          </IconButton>
          <IconButton label="Forward" onClick={() => setForwarding((v) => !v)}>
            <ForwardIcon />
          </IconButton>
          <IconButton
            label={message.pinnedAt ? 'Unpin' : 'Pin'}
            title={canActOnWhatsApp ? undefined : 'This message has no WhatsApp id on record.'}
            disabled={!canActOnWhatsApp || act.isPending}
            onClick={() => act.mutate({ action: message.pinnedAt ? 'unpin' : 'pin' })}
          >
            <PinIcon />
          </IconButton>

          <button
            type="button"
            ref={moreButtonRef}
            className="cad-icon-btn"
            title="More actions"
            aria-label="More actions"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <MoreIcon />
          </button>
          <AnchoredMenu anchorRef={moreButtonRef} open={menuOpen}>
            <button
              type="button"
              className="cad-menu-item"
              role="menuitem"
              disabled={act.isPending}
              onClick={() => {
                setMenuOpen(false)
                if (message.flaggedAt) act.mutate({ action: 'unflag' })
                else setFlagging(true)
              }}
            >
              {message.flaggedAt ? 'Unflag' : 'Flag'}
            </button>
            <button
              type="button"
              className="cad-menu-item"
              role="menuitem"
              disabled={act.isPending}
              onClick={() => {
                setMenuOpen(false)
                act.mutate({ action: message.markedInternal ? 'unmark-internal' : 'mark-internal' })
              }}
            >
              {message.markedInternal ? 'Unmark internal' : 'Mark as internal'}
            </button>
            {message.direction === 'inbound' && (
              <button
                type="button"
                className="cad-menu-item"
                role="menuitem"
                disabled={act.isPending}
                onClick={() => {
                  setMenuOpen(false)
                  if (message.ticketId) act.mutate({ action: 'detach-ticket' })
                  else setAttaching(true)
                }}
              >
                {message.ticketId ? 'Detach from ticket' : 'Attach to ticket'}
              </button>
            )}
            {message.direction === 'inbound' && !message.ticketId && (
              <button
                type="button"
                className="cad-menu-item"
                role="menuitem"
                disabled={act.isPending}
                onClick={() => {
                  setMenuOpen(false)
                  // Quoting the message rather than starting blank means opening a ticket
                  // costs one click, not a re-typed summary — still editable before saving.
                  if (!creatingTicket && !ticketTitle) setTicketTitle((message.body ?? '').slice(0, 120))
                  setCreatingTicket(true)
                }}
              >
                Create ticket
              </button>
            )}
            <button
              type="button"
              className="cad-menu-item"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false)
                setCreatingTask(true)
              }}
            >
              Create task
            </button>
            <div className="cad-menu-divider" />
            <button
              type="button"
              className="cad-menu-item"
              role="menuitem"
              disabled={act.isPending}
              onClick={() => {
                setMenuOpen(false)
                act.mutate({ action: 'delete-me' })
              }}
            >
              Delete for me
            </button>
            <button
              type="button"
              className="cad-menu-item"
              role="menuitem"
              data-tone="bad"
              disabled={!canActOnWhatsApp || act.isPending}
              title={canActOnWhatsApp ? undefined : 'This message has no WhatsApp id on record.'}
              onClick={() => {
                setMenuOpen(false)
                act.mutate({ action: 'delete' })
              }}
            >
              Delete for everyone
            </button>
          </AnchoredMenu>
        </div>
      )}

      {creatingTask && (
        <div style={{ marginTop: '6px' }}>
          <TaskForm
            conversationId={conversationId}
            messageId={message.id}
            initialTitle={(message.body ?? '').slice(0, 120)}
            members={members.map((m) => ({ userId: m.userId, name: m.name }))}
            onDone={() => {
              setCreatingTask(false)
              void refresh()
            }}
            onCancel={() => setCreatingTask(false)}
          />
        </div>
      )}

      {creatingTicket && (
        <div className="stack gap-2" style={{ marginTop: '6px' }}>
          <input
            className="cad-input"
            value={ticketTitle}
            onChange={(e) => setTicketTitle(e.target.value)}
            placeholder="What does this ticket need to resolve?"
            autoFocus
          />
          <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
            <select
              className="cad-input"
              style={{ width: 'auto' }}
              value={ticketPriority}
              onChange={(e) => setTicketPriority(e.target.value as typeof ticketPriority)}
            >
              <option value="low">Low priority</option>
              <option value="normal">Normal priority</option>
              <option value="high">High priority</option>
              <option value="urgent">Urgent</option>
            </select>
            <select
              className="cad-input"
              style={{ width: 'auto' }}
              value={ticketAssignee}
              onChange={(e) => setTicketAssignee(e.target.value)}
            >
              <option value="">Unassigned</option>
              {members.map((m) => (
                <option key={m.userId} value={m.userId}>{m.name}</option>
              ))}
            </select>
            <input
              type="datetime-local"
              className="cad-input"
              style={{ width: 'auto' }}
              value={ticketDueAt}
              onChange={(e) => setTicketDueAt(e.target.value)}
              aria-label="Due"
            />
            <button
              type="button"
              className="cad-btn"
              disabled={!ticketTitle.trim() || act.isPending}
              onClick={() =>
                act.mutate({
                  action: 'create-ticket',
                  title: ticketTitle.trim(),
                  priority: ticketPriority,
                  assigneeUserId: ticketAssignee || undefined,
                  dueAt: ticketDueAt ? new Date(ticketDueAt).toISOString() : undefined,
                })
              }
            >
              {act.isPending ? 'Creating…' : 'Create ticket'}
            </button>
            <DatePresetChips onPick={setTicketDueAt} />
            <button type="button" className="cad-btn" data-variant="text" onClick={() => setCreatingTicket(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {flagging && (
        <div className="row gap-2 wrap" style={{ marginTop: '6px', alignItems: 'center' }}>
          <input
            className="cad-input"
            style={{ width: '220px' }}
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            placeholder="Why is this being flagged? (optional)"
            autoFocus
          />
          <button
            type="button"
            className="cad-btn"
            disabled={act.isPending}
            onClick={() => act.mutate({ action: 'flag', reason: flagReason.trim() || undefined })}
          >
            Flag
          </button>
          <button type="button" className="cad-btn" data-variant="text" onClick={() => setFlagging(false)}>
            Cancel
          </button>
        </div>
      )}

      {attaching && (
        <div className="row gap-2 wrap" style={{ marginTop: '6px', alignItems: 'center' }}>
          <select className="cad-input" style={{ width: 'auto' }} value={attachTo} onChange={(e) => setAttachTo(e.target.value)}>
            <option value="">Attach to ticket…</option>
            {tickets
              .filter((t) => t.conversationId === conversationId && t.status !== 'closed')
              .map((t) => (
                <option key={t.id} value={t.id}>{t.reference} · {t.category}</option>
              ))}
          </select>
          <button
            type="button"
            className="cad-btn"
            disabled={!attachTo || act.isPending}
            onClick={() => act.mutate({ action: 'attach-ticket', ticketId: attachTo })}
          >
            Attach
          </button>
          <button type="button" className="cad-btn" data-variant="text" onClick={() => setAttaching(false)}>
            Cancel
          </button>
        </div>
      )}

      {reacting && (
        <div className="row gap-1 wrap" style={{ marginTop: '4px' }}>
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="cad-pill"
              onClick={() => act.mutate({ action: 'react', emoji })}
            >
              {emoji}
            </button>
          ))}
          {message.reactions.length > 0 && (
            <button type="button" className="cad-pill" onClick={() => act.mutate({ action: 'react', emoji: '' })}>
              Remove
            </button>
          )}
        </div>
      )}

      {forwarding && (
        <div className="row gap-2 wrap" style={{ marginTop: '6px', alignItems: 'center' }}>
          <select className="cad-input" style={{ width: 'auto' }} value={forwardTo} onChange={(e) => setForwardTo(e.target.value)}>
            <option value="">Forward to…</option>
            {conversations
              .filter((c) => c.id !== conversationId)
              .map((c) => (
                <option key={c.id} value={c.id}>{conversationTitle(c)}</option>
              ))}
          </select>
          <button
            type="button"
            className="cad-btn"
            data-variant="text"
            disabled={!forwardTo || forward.isPending}
            onClick={() => forward.mutate(forwardTo)}
          >
            Send
          </button>
        </div>
      )}

      {errorMessage && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)', marginTop: '4px' }}>{errorMessage}</p>
      )}
    </div>
  )
}

type PanelTab = 'properties' | 'ticket' | 'contact' | 'tasks'

export function Inbox({ industryName }: { industryName: string }) {
  const queryClient = useQueryClient()
  // Shares the cache with App.tsx's own session query — never a second network round trip,
  // just a read of data the shell already fetched.
  const session = useQuery({ queryKey: ['session'], queryFn: api.session })
  const isDemo = session.data?.workspace?.isDemo ?? false
  // The server rejects a viewer's /reply call outright (deliberately read-only), but the
  // composer had no matching gate — a viewer could type and hit Send only to have it bounce.
  // Same disabled-with-reason treatment as the demo composer above.
  const isViewer = session.data?.workspace?.role === 'viewer'
  const [selected, setSelected] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [isNote, setIsNote] = useState(false)
  const [media, setMedia] = useState<UploadedMedia | null>(null)
  const [mediaPreview, setMediaPreview] = useState<string | null>(null)
  const [pinning, setPinning] = useState(false)
  const [pinLat, setPinLat] = useState('')
  const [pinLng, setPinLng] = useState('')
  const [pinName, setPinName] = useState('')
  const [sendingProduct, setSendingProduct] = useState(false)
  const [rewriting, setRewriting] = useState(false)
  const [panelTab, setPanelTab] = useState<PanelTab>('properties')
  const [activeViewId, setActiveViewId] = useState('')
  const [buildingView, setBuildingView] = useState(false)
  const [editingViewId, setEditingViewId] = useState<string | null>(null)
  const [viewMatch, setViewMatch] = useState<'all' | 'any'>('all')
  const [viewConditions, setViewConditions] = useState<ConditionEntry[]>([])
  const [viewName, setViewName] = useState('')
  const [viewShared, setViewShared] = useState(false)
  const [viewIcon, setViewIcon] = useState('')
  const [viewTeamId, setViewTeamId] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkLabelId, setBulkLabelId] = useState('')
  const [bulkTag, setBulkTag] = useState('')
  const [bulkTeamId, setBulkTeamId] = useState('')
  const [bulkAssignee, setBulkAssignee] = useState('')
  const [bulkResult, setBulkResult] = useState<string | null>(null)
  const [csvError, setCsvError] = useState<string | null>(null)
  // The landing view used to show this list twice — once as threads, once as a "needs a reply"
  // table. `filterTab` lets the one list stand in for both: the table's rows and the thread
  // list's rows were always the same underlying conversations, just rendered two ways.
  const [filterTab, setFilterTab] = useState<'all' | 'needs-reply' | 'breaching' | 'archived'>('all')
  // Checkboxes used to render on every row all the time, for a feature (bulk actions) most
  // reads of the list never touch. They only exist once this is on.
  const [selectMode, setSelectMode] = useState(false)

  const savedViews = useQuery({ queryKey: ['saved-views'], queryFn: api.savedViews, retry: false })
  const team = useQuery({ queryKey: ['team'], queryFn: api.team, retry: false })
  // Same reasoning as Contacts' segment builder: a "Labels" condition typed as free text
  // silently never matches a real label it doesn't spell exactly right.
  const conversationFields = CONVERSATION_FIELDS.map((f) =>
    f.key === 'labels' && team.data?.labels.length ? { ...f, options: team.data.labels.map((l) => l.name) } : f,
  )

  const toggleSelected = (id: string) =>
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const bulk = useMutation({
    mutationFn: (body: Parameters<typeof api.bulkInboxAction>[0]) => api.bulkInboxAction(body),
    onMutate: () => setBulkResult(null),
    onSuccess: (result) => {
      const parts = [`${result.updated} updated`]
      if (result.skipped) parts.push(`${result.skipped} skipped (no open ticket)`)
      if (result.failures?.length) parts.push(`${result.failures.length} could not close`)
      if (result.unmatched) parts.push(`${result.unmatched} row${result.unmatched === 1 ? '' : 's'} matched no selected chat`)
      setBulkResult(parts.join(', '))
      setSelectedIds(new Set())
      void queryClient.invalidateQueries({ queryKey: ['inbox'] })
      if (selected) void queryClient.invalidateQueries({ queryKey: ['conversation', selected] })
    },
  })

  // Same "am I updating an id or minting one" branch Contacts.tsx's segment builder uses —
  // editing a view is the identical builder pre-filled from the view being changed, not a
  // second screen.
  const saveView = useMutation({
    mutationFn: async (): Promise<{ id: string }> =>
      editingViewId
        ? await api
            .updateSavedView(editingViewId, { name: viewName, match: viewMatch, conditions: viewConditions, icon: viewIcon || null })
            .then(() => ({ id: editingViewId }))
        : await api.createSavedView({
            name: viewName,
            shared: viewShared,
            match: viewMatch,
            conditions: viewConditions,
            icon: viewIcon || null,
            teamId: viewShared ? null : viewTeamId || null,
          }),
    onSuccess: (result) => {
      setBuildingView(false)
      setEditingViewId(null)
      setViewName('')
      setViewConditions([])
      setViewIcon('')
      setViewTeamId('')
      void queryClient.invalidateQueries({ queryKey: ['saved-views'] })
      setActiveViewId(result.id)
    },
  })

  const deleteView = useMutation({
    mutationFn: (id: string) => api.deleteSavedView(id),
    onSuccess: () => {
      setActiveViewId((current) => (current === deleteView.variables ? '' : current))
      void queryClient.invalidateQueries({ queryKey: ['saved-views'] })
    },
  })

  const inbox = useQuery({
    // Archived state is a separate server-side filter, not a client-side one like the other
    // tabs — the point of archiving is to stop paying attention to a chat, so it should not
    // still be part of the same 50-row window "All" pages through. The query key carries the
    // flag so switching into or out of the tab refetches the right set.
    queryKey: ['inbox', filterTab === 'archived'],
    queryFn: () => api.inbox(filterTab === 'archived'),
    // Messages arrive from outside the browser, so the list polls rather than waiting for
    // an action. A socket would be better and is the natural upgrade once one exists.
    refetchInterval: 15_000,
    retry: false,
  })

  const thread = useQuery({
    queryKey: ['conversation', selected],
    queryFn: () => api.conversation(selected!),
    enabled: Boolean(selected),
    refetchInterval: 15_000,
  })

  // Holds whatever the switcher and saved view have filtered to, for the keyboard handler
  // above to read at keypress time.
  const visibleRef = useRef<InboxConversation[]>([])


  const { listRef, atBottom, missedWhileAway, scrollToBottom } = useStickToNewest(
    selected,
    thread.data?.messages.length ?? 0,
  )

  /**
   * Where the "unread" rule goes, if the thread had any when it was opened.
   *
   * Captured once per thread rather than read live: opening a conversation marks it read a beat
   * later (see the effect below), so a live count would drop to zero and pull the divider out
   * from under the messages the agent is in the middle of reading. WhatsApp keeps its rule on
   * screen for the same reason.
   */
  /**
   * Find-in-conversation.
   *
   * Runs against the messages already loaded rather than the server, because the thread
   * endpoint caps at 200 and those are the only ones this screen has. That makes it instant and
   * exact for what is on screen — and incomplete for a conversation longer than the cap, which
   * the UI says out loud rather than quietly returning "no matches" for a message that exists.
   */
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [matchCursor, setMatchCursor] = useState(0)

  const searchMatches = (() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return [] as string[]
    return (thread.data?.messages ?? [])
      .filter((m) => (m.body ?? '').toLowerCase().includes(q))
      .map((m) => m.id)
  })()

  // A shrinking result set must not leave the cursor pointing past the end.
  useEffect(() => {
    setMatchCursor(0)
  }, [searchQuery, selected])

  const activeMatchId = searchMatches.length > 0 ? searchMatches[matchCursor % searchMatches.length] : null

  useEffect(() => {
    if (!activeMatchId) return
    document
      .querySelector(`[data-message-id="${activeMatchId}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'auto' })
  }, [activeMatchId])

  // Closing search must not leave a stale highlight behind.
  useEffect(() => {
    if (!searchOpen) {
      setSearchQuery('')
      setMatchCursor(0)
    }
  }, [searchOpen])


  const [unreadAnchor, setUnreadAnchor] = useState<number>(0)

  /**
   * Keyboard navigation for a queue worked all day.
   *
   * Arrow keys step through the threads currently visible — whatever the switcher and saved
   * view have filtered to, so the keyboard never reaches a row the eye cannot see. Escape
   * backs out; `/` opens find-in-conversation.
   *
   * Registered before this component's `inbox.isError` early return, and reading the visible
   * list through a ref rather than a dependency. Sitting below that return made the hook
   * count drop on any failed poll — React throws "Rendered fewer hooks than expected" in
   * production too, which took the whole Inbox down instead of showing its error card. The
   * ref also stops the listener being torn down and re-added on every single render.
   *
   * The guard matters more than the shortcuts: a bare-key binding that fires while someone is
   * typing a reply is a data-loss bug, not a convenience. Anything originating in a field, or
   * carrying a modifier the browser owns, is left alone.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target as HTMLElement | null
      const typing =
        !!t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)

      if (e.key === 'Escape') {
        // Escape inside the search field is that field's business; it closes search itself.
        if (typing) return
        if (searchOpen) setSearchOpen(false)
        else if (selected) setSelected(null)
        return
      }

      if (typing) return

      if (e.key === '/' && selected) {
        e.preventDefault()
        setSearchOpen(true)
        return
      }

      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      const ids = visibleRef.current.map((c) => c.id)
      if (ids.length === 0) return
      e.preventDefault()
      const at = selected ? ids.indexOf(selected) : -1
      const next =
        e.key === 'ArrowDown'
          ? Math.min(at + 1, ids.length - 1)
          : at <= 0
            ? 0
            : at - 1
      setSelected(ids[next])
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selected, searchOpen])
  useEffect(() => {
    const count = data?.conversations.find((c) => c.id === selected)?.unreadCount ?? 0
    setUnreadAnchor(count)
    // Only when the thread changes — deliberately not when the count updates underneath.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  // Opening a thread is what "read" means here — the same moment WhatsApp's own client
  // would send a read receipt, so the two stay in step instead of drifting apart.
  useEffect(() => {
    if (!selected) return
    void api.markRead(selected).then(() => queryClient.invalidateQueries({ queryKey: ['inbox'] }))
  }, [selected, queryClient])

  const replies = useQuery({ queryKey: ['quick-replies'], queryFn: api.quickReplies, retry: false })

  // Only while the draft looks like a command, so normal typing is never interrupted.
  const suggestions =
    draft.startsWith('/') && !isNote
      ? (replies.data?.replies ?? []).filter((r) =>
          r.shortcut.toLowerCase().startsWith(draft.toLowerCase()),
        )
      : []

  // Only in a private note — tagging a teammate in a reply that reaches the customer would
  // make no sense.
  const pendingMention = isNote ? computeMentionQuery(draft) : null
  const mentionSuggestions =
    pendingMention !== null ? matchMentionCandidates(pendingMention, team.data?.members ?? []) : []

  const upload = useMutation({
    mutationFn: api.uploadMedia,
    onSuccess: (result) => setMedia(result),
  })

  const clearAttachment = () => {
    setMedia(null)
    setMediaPreview(null)
    upload.reset()
  }

  const onAttach = (file: File | undefined) => {
    if (!file) return
    upload.reset()
    // Previewed from the local file rather than from storage — the bytes are already here,
    // and a round trip would only add a spinner. Same trade the broadcast composer makes.
    setMediaPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : null)
    upload.mutate(file)
  }

  /**
   * Paste a screenshot straight into the reply.
   *
   * The single most common attachment in support work is an image already on the clipboard —
   * a screenshot of the error, a photo the customer sent elsewhere. Routing it through the file
   * picker means saving it to disk first, finding it again, and deleting it later. The
   * clipboard already has the bytes.
   *
   * Only intercepts when the clipboard actually carries a file. Pasting text stays plain text,
   * because hijacking an ordinary paste would be far worse than not having this at all.
   */
  const onComposerPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const file = Array.from(event.clipboardData?.files ?? [])[0]
    if (!file) return
    event.preventDefault()
    onAttach(file)
  }

  // Dropping onto the conversation. Counted rather than a boolean: dragging across a child
  // element fires leave-then-enter, and a naive flag flickers the overlay off mid-drag.
  const [dragDepth, setDragDepth] = useState(0)
  const dragProps = {
    onDragEnter: (e: React.DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return
      e.preventDefault()
      setDragDepth((d) => d + 1)
    },
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return
      e.preventDefault()
    },
    onDragLeave: () => setDragDepth((d) => Math.max(0, d - 1)),
    onDrop: (e: React.DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return
      e.preventDefault()
      setDragDepth(0)
      onAttach(e.dataTransfer.files[0])
    },
  }

  const clearPin = () => {
    setPinning(false)
    setPinLat('')
    setPinLng('')
    setPinName('')
  }

  // A pin is only a pin once both coordinates parse; half of one is not worth sending, and
  // the server would refuse it anyway.
  const latitude = Number(pinLat)
  const longitude = Number(pinLng)
  const pin =
    pinning && pinLat.trim() && pinLng.trim() && Number.isFinite(latitude) && Number.isFinite(longitude)
      ? { latitude, longitude, ...(pinName.trim() ? { name: pinName.trim() } : {}) }
      : null

  // A private note never leaves the system, so a pin in one would be coordinates nobody sees.
  const outboundPin = isNote ? null : pin
  const canSend = Boolean((draft.trim() || media || outboundPin) && !isDemo && !isViewer)

  const reply = useMutation({
    mutationFn: () =>
      api.reply(selected!, { body: draft, isPrivateNote: isNote, media, location: outboundPin }),
    onSuccess: async () => {
      setDraft('')
      clearAttachment()
      clearPin()
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['conversation', selected] }),
        queryClient.invalidateQueries({ queryKey: ['inbox'] }),
      ])
    },
  })

  // Rewrites the draft in place — translate-then-send, not a preview kept alongside the
  // original, since what gets sent is whatever is left in the box when Send is pressed.
  const translateDraft = useMutation({
    mutationFn: (targetLanguage: string) => api.translateMessage(selected!, draft, targetLanguage),
    onSuccess: (result) => setDraft(result.translated),
  })

  // Same in-place shape as translateDraft, deliberately not combined with it — a style rewrite
  // and a language translation are two different requests an agent should be able to make
  // independently, not two options buried in one picker.
  const rewriteDraft = useMutation({
    mutationFn: (style: RewriteStyle) => api.rewriteMessage(selected!, draft, style),
    onSuccess: (result) => {
      setDraft(result.rewritten)
      setRewriting(false)
    },
  })

  // Loaded lazily — only once the picker is actually opened — so an inbox with no catalogue
  // never pays for a query nobody asked for.
  const products = useQuery({ queryKey: ['products'], queryFn: api.products, enabled: sendingProduct })

  /**
   * Generates a fresh payment link and drops the product's name/description/price/link into
   * the draft, with its image attached — the same "attach, then review, then Send" shape every
   * other attachment already goes through here. Not a native WhatsApp product card: there is no
   * such message type reachable through this connection, see Products.tsx's own note.
   */
  const sendProduct = useMutation({
    mutationFn: (product: ProductView) =>
      api.createProductPaymentLink(product.id, selected ?? undefined).then((link) => ({ product, link })),
    onSuccess: ({ product, link }) => {
      const price = `${(product.priceMinor / 100).toFixed(2)} ${product.currency}`
      const lines = [product.name, product.description, price, link.url].filter(Boolean)
      setDraft(lines.join('\n'))
      if (product.mediaKey) {
        setMedia({
          key: product.mediaKey,
          kind: product.mediaKind ?? 'image',
          mime: product.mediaMime ?? 'image/jpeg',
          fileName: product.mediaFileName ?? 'image',
          size: product.mediaSize ?? 0,
        })
        setMediaPreview(mediaUrl(product.mediaKey))
      }
      setSendingProduct(false)
    },
  })

  const [scheduling, setScheduling] = useState(false)
  const [scheduleAt, setScheduleAt] = useState('')
  const [scheduleRecurrence, setScheduleRecurrence] = useState<RecurrenceState>(EMPTY_RECURRENCE)

  const scheduledForChat = useQuery({
    queryKey: ['scheduled-messages', selected],
    queryFn: () => api.scheduledMessagesForChat(selected!),
    enabled: Boolean(selected),
    retry: false,
  })

  const scheduleMessage = useMutation({
    mutationFn: () =>
      api.scheduleMessage(selected!, {
        body: draft,
        scheduledFor: new Date(scheduleAt).toISOString(),
        ...recurrencePayload(scheduleRecurrence),
      }),
    onSuccess: () => {
      setDraft('')
      setScheduling(false)
      setScheduleAt('')
      setScheduleRecurrence(EMPTY_RECURRENCE)
      void queryClient.invalidateQueries({ queryKey: ['scheduled-messages', selected] })
    },
  })

  const cancelScheduled = useMutation({
    mutationFn: (id: string) => api.cancelScheduledMessage(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['scheduled-messages', selected] }),
  })

  if (inbox.isError) {
    const message = inbox.error instanceof ApiError ? inbox.error.message : 'Inbox unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const data = inbox.data
  const openTickets = data?.tickets ?? []
  const breached = openTickets.filter((t) => t.breached)
  // The one status signal that belongs on every row in the list itself, not just the "Needs a
  // reply" table — see the note on `.cad-thread-breach` for why nothing else earns a dot here.
  const breachedConversationIds = new Set(breached.map((t) => t.conversationId))
  const activeTicket = thread.data?.tickets[0]

  const views = savedViews.data?.views ?? []
  const activeView = views.find((view) => view.id === activeViewId)
  const needsReplyConversationIds = new Set(openTickets.map((t) => t.conversationId))
  const segmentFiltered = activeView
    ? (data?.conversations ?? []).filter((conversation) =>
        matchesSegment(activeView.segment, conversationSubject(conversation, openTickets)),
      )
    : (data?.conversations ?? [])
  const visibleConversations =
    filterTab === 'needs-reply'
      ? segmentFiltered.filter((c) => needsReplyConversationIds.has(c.id))
      : filterTab === 'breaching'
        ? segmentFiltered.filter((c) => breachedConversationIds.has(c.id))
        : segmentFiltered
  visibleRef.current = visibleConversations


  return (
    <div className="cad-inbox">
      <header className="cad-inbox-bar">
        <div className="stack gap-1" style={{ minWidth: 0 }}>
          <span className="cad-eyebrow">{industryName} · Inbox</span>
          <h1 className="cad-title cad-inbox-title">Conversations</h1>
        </div>
        {/* The All / Needs reply / Breaching / Archived switcher used to sit on a row of its
            own directly below this bar. It is ~126px of controls, and the bar had ~545px of
            empty spacer running through the middle of it — so the row cost a full 30px plus a
            12px gap to display something that already had somewhere to go. Saved views stay on
            their own row below, because that list grows with however many a workspace has
            defined and would otherwise push these controls around. */}
        <div className="cad-inbox-switcher-slot">
          <div className="cad-switcher" role="tablist" aria-label="Inbox filter">
            <SwitcherTab label="All" active={filterTab === 'all'} onClick={() => setFilterTab('all')}>
              <AllChatsIcon />
            </SwitcherTab>
            <SwitcherTab
              label="Needs reply"
              active={filterTab === 'needs-reply'}
              count={openTickets.length}
              onClick={() => setFilterTab('needs-reply')}
            >
              <NeedsReplyIcon />
            </SwitcherTab>
            <SwitcherTab
              label="Breaching"
              tone="bad"
              active={filterTab === 'breaching'}
              count={breached.length}
              onClick={() => setFilterTab('breaching')}
            >
              <BreachingIcon />
            </SwitcherTab>
            <SwitcherTab label="Archived" active={filterTab === 'archived'} onClick={() => setFilterTab('archived')}>
              <ArchivedIcon />
            </SwitcherTab>
          </div>
        </div>
        <span className="grow" />
        <div className="row gap-2 wrap" style={{ alignItems: 'center', justifyContent: 'flex-end' }}>
          {data?.masked && <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>Numbers hidden</span>}
          <button
            type="button"
            className="cad-btn"
            data-variant={selectMode ? undefined : 'quiet'}
            onClick={() => {
              setSelectMode((v) => !v)
              setSelectedIds(new Set())
            }}
          >
            {selectMode ? 'Done selecting' : 'Select'}
          </button>
          <div className="cad-views-menu">
            <button
              type="button"
              className="cad-btn"
              data-variant="quiet"
              onClick={() => {
                // Toggling this open fresh (not via "Edit view") must not resume whatever view
                // was last being edited — that stale id is only cleared on Save/Cancel, not on
                // simply closing the popover with this same button.
                setBuildingView((v) => {
                  const opening = !v
                  if (opening) {
                    setEditingViewId(null)
                    setViewName('')
                    setViewConditions([])
                    setViewIcon('')
                    setViewTeamId('')
                  }
                  return opening
                })
              }}
            >
              Views ▾
            </button>
            {buildingView && (
              <section className="cad-card cad-inbox-builder">
                <div className="stack gap-4">
                  <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
                    <h2 className="cad-section-title">{editingViewId ? 'Edit view' : 'Save a view'}</h2>
                    <div className="row gap-2 wrap">
                      {VIEW_PRESETS.map((preset) => (
                        <button
                          key={preset.name}
                          type="button"
                          className="cad-pill"
                          onClick={() => {
                            setViewMatch(preset.segment.match)
                            setViewConditions(preset.segment.conditions)
                            if (!viewName) setViewName(preset.name)
                          }}
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {viewConditions.length === 0 ? (
                    <button
                      type="button"
                      className="cad-btn"
                      data-variant="quiet"
                      onClick={() =>
                        setViewConditions([{ field: CONVERSATION_FIELDS[0].key, operator: 'is', value: '' }])
                      }
                    >
                      Add condition
                    </button>
                  ) : (
                    <SegmentBuilder
                      fields={conversationFields}
                      match={viewMatch}
                      conditions={viewConditions}
                      onChange={(next) => {
                        setViewMatch(next.match)
                        setViewConditions(next.conditions)
                      }}
                    />
                  )}

                  <div className="field-grid">
                    <div className="cad-field">
                      <label htmlFor="view-name">Name this view</label>
                      <input
                        id="view-name"
                        className="cad-input"
                        value={viewName}
                        onChange={(e) => setViewName(e.target.value)}
                        placeholder="Urgent and open"
                      />
                    </div>
                  </div>

                  <div className="stack gap-2">
                    <span className="cad-label">Icon (optional)</span>
                    <div className="row gap-1 wrap">
                      {VIEW_ICONS.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          className="cad-pill"
                          data-tone={viewIcon === icon ? 'accent' : undefined}
                          onClick={() => setViewIcon(viewIcon === icon ? '' : icon)}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Who can see it is set once, at creation — editing an existing view changes
                      its conditions, not its audience, so this only shows for a new one. */}
                  {!editingViewId && (
                    <div className="stack gap-2">
                      <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={viewShared}
                          onChange={(e) => {
                            setViewShared(e.target.checked)
                            if (e.target.checked) setViewTeamId('')
                          }}
                        />
                        <span className="cad-muted">Share with the whole workspace</span>
                      </label>
                      {!viewShared && (team.data?.teams.length ?? 0) > 0 && (
                        <div className="cad-field" style={{ maxWidth: '220px' }}>
                          <label htmlFor="view-team">Or share with one team</label>
                          <select
                            id="view-team"
                            className="cad-input"
                            value={viewTeamId}
                            onChange={(e) => setViewTeamId(e.target.value)}
                          >
                            <option value="">Just me</option>
                            {team.data?.teams.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                  {saveView.error && (
                    <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                      {saveView.error instanceof ApiError || saveView.error instanceof Error
                        ? saveView.error.message
                        : 'Could not save that view.'}
                    </p>
                  )}

                  <div className="row gap-2 wrap">
                    <button
                      type="button"
                      className="cad-btn"
                      disabled={!viewName.trim() || !viewConditions.some(isLiveEntry) || saveView.isPending}
                      onClick={() => saveView.mutate()}
                    >
                      {saveView.isPending ? 'Saving…' : editingViewId ? 'Save changes' : 'Save view'}
                    </button>
                    <button
                      type="button"
                      className="cad-btn"
                      data-variant="quiet"
                      onClick={() => {
                        setBuildingView(false)
                        setEditingViewId(null)
                        setViewConditions([])
                        setViewName('')
                        setViewIcon('')
                        setViewTeamId('')
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>
      </header>

      {/* Only the saved-view pills live here now, and only when a workspace has any — this row
          no longer exists at all for the common case. */}
      {views.length > 0 && (
        <div className="cad-inbox-filters row gap-2 wrap" style={{ alignItems: 'center' }}>
          <button
            type="button"
            className="cad-pill"
            data-tone={activeViewId === '' ? 'accent' : undefined}
            onClick={() => setActiveViewId('')}
          >
            All views
          </button>
            {views.map((view) => (
              <button
                key={view.id}
                type="button"
                className="cad-pill"
                data-tone={activeViewId === view.id ? 'accent' : undefined}
                onClick={() => setActiveViewId(view.id)}
                title={view.shared ? `${view.name} · shared with everyone` : view.teamId ? `${view.name} · shared with a team` : view.name}
              >
                {view.icon ? `${view.icon} ` : ''}
                {view.name}
              </button>
            ))}
            {activeView && (activeView.mine || savedViews.data?.canShare) && (
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                onClick={() => {
                  setEditingViewId(activeView.id)
                  setViewName(activeView.name)
                  setViewMatch(activeView.segment.match)
                  setViewConditions(activeView.segment.conditions)
                  setViewIcon(activeView.icon ?? '')
                  setBuildingView(true)
                }}
              >
                Edit view
              </button>
            )}
            {activeView && (activeView.mine || savedViews.data?.canShare) && (
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                onClick={() => {
                  if (confirm(`Delete the "${activeView.name}" view?

${activeView.shared ? 'This view is shared, so it disappears for everyone on the team.' : 'This only removes your own saved filter.'} No chats, tickets or messages are touched.`)) deleteView.mutate(activeView.id)
                }}
              >
                Delete view
              </button>
            )}
        </div>
      )}

      <div className={selected ? 'inbox-grid' : 'inbox-grid inbox-grid--no-side'}>
        <section className="cad-card">
          <div className="stack gap-3">
            <div className="row between gap-2" style={{ alignItems: 'center' }}>
              <h2 className="cad-section-title">Threads</h2>
              {selectMode && visibleConversations.length > 0 && (
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  onClick={() =>
                    setSelectedIds((current) =>
                      current.size === visibleConversations.length
                        ? new Set()
                        : new Set(visibleConversations.map((item) => item.id)),
                    )
                  }
                >
                  {selectedIds.size === visibleConversations.length ? 'Clear selection' : 'Select all'}
                </button>
              )}
            </div>

            {bulkResult && selectedIds.size === 0 && (
              <p className="cad-note">{bulkResult}</p>
            )}

            {selectedIds.size > 0 && (
              <div className="cad-card" style={{ padding: '10px 12px' }}>
                <div className="stack gap-2">
                  <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                    {selectedIds.size} selected
                  </span>
                  <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                    <div className="cad-bulk-group row gap-2" style={{ alignItems: 'center' }}>
                      <select
                        className="cad-input"
                        style={{ width: 'auto' }}
                        value={bulkLabelId}
                        onChange={(e) => setBulkLabelId(e.target.value)}
                      >
                        <option value="">Label…</option>
                        {(team.data?.labels ?? []).map((label) => (
                          <option key={label.id} value={label.id}>{label.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        disabled={!bulkLabelId || bulk.isPending}
                        onClick={() =>
                          bulk.mutate({ action: 'add_label', conversationIds: [...selectedIds], labelId: bulkLabelId })
                        }
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        disabled={!bulkLabelId || bulk.isPending}
                        onClick={() =>
                          bulk.mutate({ action: 'remove_label', conversationIds: [...selectedIds], labelId: bulkLabelId })
                        }
                      >
                        Remove
                      </button>
                    </div>

                    {/* Team first, then optionally one member of it — "assign to Support" is a
                        complete answer on its own; naming a person narrows it further. Picking a
                        team resets any already-chosen individual outside it. */}
                    <div className="cad-bulk-group row gap-2" style={{ alignItems: 'center' }}>
                      <select
                        className="cad-input"
                        style={{ width: 'auto' }}
                        value={bulkTeamId}
                        onChange={(e) => {
                          setBulkTeamId(e.target.value)
                          const teamMemberIds = team.data?.teams.find((t) => t.id === e.target.value)?.memberIds ?? []
                          if (bulkAssignee && !teamMemberIds.includes(bulkAssignee)) setBulkAssignee('')
                        }}
                      >
                        <option value="">Any team…</option>
                        {(team.data?.teams ?? []).map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                      <select
                        className="cad-input"
                        style={{ width: 'auto' }}
                        value={bulkAssignee}
                        onChange={(e) => setBulkAssignee(e.target.value)}
                      >
                        <option value="">
                          {bulkTeamId ? 'Anyone on the team' : 'Assign to…'}
                        </option>
                        {(team.data?.members ?? [])
                          .filter((m) => {
                            const teamMemberIds = team.data?.teams.find((t) => t.id === bulkTeamId)?.memberIds
                            return !bulkTeamId || teamMemberIds?.includes(m.userId)
                          })
                          .map((m) => (
                            <option key={m.userId} value={m.userId}>{m.name}</option>
                          ))}
                      </select>
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        disabled={(!bulkAssignee && !bulkTeamId) || bulk.isPending}
                        onClick={() =>
                          bulk.mutate({
                            action: 'assign',
                            conversationIds: [...selectedIds],
                            assigneeUserId: bulkAssignee || null,
                            teamId: bulkTeamId || null,
                          })
                        }
                      >
                        Assign
                      </button>
                    </div>

                    <div className="cad-bulk-group row gap-2" style={{ alignItems: 'center' }}>
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        disabled={bulk.isPending}
                        onClick={() => bulk.mutate({ action: 'mark_read', conversationIds: [...selectedIds] })}
                      >
                        Mark read
                      </button>
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        disabled={bulk.isPending}
                        onClick={() => bulk.mutate({ action: 'mark_unread', conversationIds: [...selectedIds] })}
                      >
                        Mark unread
                      </button>
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        disabled={bulk.isPending}
                        onClick={() =>
                          bulk.mutate({
                            action: filterTab === 'archived' ? 'unarchive' : 'archive',
                            conversationIds: [...selectedIds],
                          })
                        }
                      >
                        {filterTab === 'archived' ? 'Unarchive' : 'Archive'}
                      </button>
                    </div>

                    <div className="cad-bulk-group row gap-2" style={{ alignItems: 'center' }}>
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        disabled={bulk.isPending}
                        onClick={() => bulk.mutate({ action: 'close', conversationIds: [...selectedIds] })}
                      >
                        Close tickets
                      </button>

                      <input
                        className="cad-input"
                        style={{ width: '160px' }}
                        placeholder="Tag contacts…"
                        value={bulkTag}
                        onChange={(e) => setBulkTag(e.target.value)}
                      />
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        disabled={!bulkTag.trim() || bulk.isPending}
                        onClick={() =>
                          bulk.mutate({ action: 'tag_contact', conversationIds: [...selectedIds], tag: bulkTag.trim() })
                        }
                      >
                        Tag
                      </button>
                    </div>

                    <div className="cad-bulk-group row gap-2" style={{ alignItems: 'center' }}>
                      <a
                        className="cad-btn"
                        data-variant="text"
                        href={api.exportSelectedUrl('conversations', [...selectedIds])}
                        download
                      >
                        Export
                      </a>
                      <input
                        id="inbox-bulk-csv"
                        type="file"
                        accept=".csv,.tsv,.txt,text/csv,text/plain"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          e.target.value = ''
                          if (!file) return
                          setCsvError(null)
                          const text = await file.text()
                          const parsed = parseCsv(text, detectDelimiter(text))
                          if (parsed.length < 2) {
                            setCsvError('That file has no data rows.')
                            return
                          }
                          const [header, ...dataRows] = parsed
                          const phoneCol = Math.max(
                            0,
                            header.findIndex((h) => h.trim().toLowerCase() === 'phone'),
                          )
                          const rows = dataRows
                            .filter((r) => r.some((cell) => cell.trim()))
                            .map((r) => ({
                              phone: r[phoneCol] ?? '',
                              values: Object.fromEntries(
                                header.map((h, i) => [h, r[i] ?? '']).filter(([h], i) => i !== phoneCol && h.trim()),
                              ),
                            }))
                          bulk.mutate({ action: 'apply_csv', conversationIds: [...selectedIds], rows })
                        }}
                      />
                      <label htmlFor="inbox-bulk-csv" className="cad-btn" data-variant="text" role="button">
                        Upload labels/properties CSV
                      </label>
                    </div>
                  </div>
                  {csvError && (
                    <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{csvError}</p>
                  )}
                  {bulkResult && <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{bulkResult}</span>}
                </div>
              </div>
            )}

            {visibleConversations.length === 0 ? (
              activeView ? (
                <p className="cad-muted">Nothing in this view right now.</p>
              ) : (
                /* Onboarding covers industry, plan and database but never mentions connecting a
                   number, and this is where a new workspace lands straight afterwards. The old
                   copy described the precondition without offering it, leaving the only route a
                   small unlabelled chip in the top bar. */
                <div className="stack gap-2">
                  <p className="cad-muted">
                    Nothing yet. Messages appear here once a WhatsApp number is connected and
                    delivering.
                  </p>
                  <div>
                    <a className="cad-btn" href="#numbers">
                      Connect a WhatsApp number
                    </a>
                  </div>
                </div>
              )
            ) : (
              <div className="cad-thread-list">
                {visibleConversations.map((item) => (
                  <div
                    key={item.id}
                    className="cad-thread-row"
                    data-selected={selected === item.id}
                  >
                    {selectMode && (
                      <input
                        type="checkbox"
                        className="cad-thread-check"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelected(item.id)}
                        aria-label={`Select ${conversationTitle(item)}`}
                      />
                    )}
                    <div className="cad-thread-body">
                      <button
                        type="button"
                        className="cad-thread"
                        onClick={() => setSelected(item.id)}
                      >
                        <div className="row between gap-2" style={{ alignItems: 'baseline' }}>
                          <span className="row gap-1" style={{ alignItems: 'baseline', minWidth: 0 }}>
                            {breachedConversationIds.has(item.id) && (
                              <span className="cad-dot cad-thread-breach" title="Breaching its reply target" />
                            )}
                            <span className="cad-thread-title">{conversationTitle(item)}</span>
                          </span>
                          <span className="row gap-1" style={{ alignItems: 'baseline', flexShrink: 0 }}>
                            <AckMark ack={item.lastOutboundAck ?? null} />
                            <span className="cad-thread-time">{shortTime(item.lastMessageAt)}</span>
                          </span>
                        </div>
                        <div className="row between gap-2" style={{ alignItems: 'center' }}>
                          <span className="cad-thread-preview">
                            {item.lastMessagePreview ?? 'No messages yet'}
                          </span>
                          {/* Every label a chat carries used to render as its own text chip below this
                              row, capped at three but still capable of wrapping the row to a third line.
                              One dot per label — color hashed from the name, full name on hover — holds
                              the row to exactly two lines regardless of how many labels a chat has. */}
                          <span className="row gap-1" style={{ flexShrink: 0, alignItems: 'center' }}>
                            {item.labels && item.labels.length > 0 && (
                              <span className="row gap-1" style={{ alignItems: 'center' }}>
                                {item.labels.slice(0, 5).map((label) => (
                                  <span
                                    key={label}
                                    className="cad-dot cad-thread-label-dot"
                                    title={label}
                                    style={{ color: `hsl(${labelHue(label)}, 55%, 45%)` }}
                                  />
                                ))}
                                {item.labels.length > 5 && (
                                  <span className="cad-thread-label-overflow" title={item.labels.slice(5).join(', ')}>
                                    +{item.labels.length - 5}
                                  </span>
                                )}
                              </span>
                            )}
                            {item.assignedTo && (() => {
                              const assignee = team.data?.members.find((m) => m.userId === item.assignedTo)
                              return (
                                <span
                                  className="cad-thread-assignee"
                                  title={assignee ? `Assigned to ${assignee.name}` : 'Assigned'}
                                >
                                  {(assignee?.name ?? '?')[0]?.toUpperCase()}
                                </span>
                              )
                            })()}
                            <span className="cad-thread-kind">
                              {item.kind === 'group' ? 'Group' : 'Direct'}
                            </span>
                            {item.unreadCount > 0 && (
                              <span className="cad-pill" data-tone="accent">{item.unreadCount}</span>
                            )}
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="cad-card">
          {!selected ? (
            <p className="cad-muted">Pick a thread to read it.</p>
          ) : thread.isLoading ? (
            <div style={{ height: '200px' }} />
          ) : thread.data ? (
            <div className="stack gap-3 cad-thread-dropzone" data-dragging={dragDepth > 0} {...dragProps}>
              {dragDepth > 0 && (
                <div className="cad-drop-overlay" aria-hidden="true">
                  <span>Drop to attach</span>
                </div>
              )}
              <div className="row between wrap gap-2 cad-thread-header" style={{ alignItems: 'center' }}>
                <button
                  type="button"
                  className="cad-btn cad-thread-back"
                  data-variant="text"
                  // Selecting a thread has never had a way back except re-navigating — this is
                  // the one control that clears it, and it doubles as the "close thread" action
                  // on the single-column layout below 820px, where the list is off-screen.
                  //
                  // Hidden by CSS once the list pane is on screen beside this one: there is
                  // nothing to go "back" to when the destination is already visible, and a
                  // full-height button was the tallest thing in a header that had to wrap.
                  onClick={() => setSelected(null)}
                  aria-label="Back to the thread list"
                  title="Back to the thread list"
                >
                  ← Back
                </button>
                <h2 className="cad-section-title">
                  {conversationTitle(thread.data.conversation)}
                </h2>
                {/* The header carries exceptions, not steady state. Reference, category,
                    priority, status and assignee all live in the Details panel's Ticket tab,
                    which already listed most of them — repeating them here cost two wrapped
                    rows above the conversation to say what a click away already said.
                    What stays is what changes the next thirty seconds of an agent's work: an
                    unanswered ticket running out of time. Once it has been answered, SlaPill
                    renders "Answered", which is steady state and belongs in the panel too. */}
                {activeTicket && !activeTicket.firstResponseAt && <SlaPill ticket={activeTicket} />}
                <LastCustomerMessagePill messages={thread.data.messages} />
                <PresenceIndicator conversationId={selected} />
                <button
                  type="button"
                  className="cad-icon-btn"
                  onClick={() => setSearchOpen((v) => !v)}
                  aria-expanded={searchOpen}
                  title="Find in this conversation"
                  aria-label="Find in this conversation"
                >
                  <SearchIcon />
                </button>
                <VisibilityIndicator
                  conversationId={selected}
                  labels={data?.conversations.find((c) => c.id === selected)?.labels ?? []}
                />
                <AiSummaryButton conversationId={selected} />
                <AiDraftReplyButton
                  conversationId={selected}
                  question={
                    [...thread.data.messages]
                      .reverse()
                      .find((m) => m.direction === 'inbound' && !m.isPrivateNote && m.body)?.body ?? null
                  }
                  onUseDraft={setDraft}
                />
              </div>

              {searchOpen && (
                <div className="cad-thread-search row gap-2" style={{ alignItems: 'center' }}>
                  <input
                    className="cad-input"
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setSearchOpen(false)
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        if (searchMatches.length) setMatchCursor((c) => (c + (e.shiftKey ? -1 : 1) + searchMatches.length) % searchMatches.length)
                      }
                    }}
                    placeholder="Find in this conversation"
                    aria-label="Find in this conversation"
                  />
                  <span className="cad-muted" style={{ fontSize: 'var(--cad-text-xs)', whiteSpace: 'nowrap' }}>
                    {searchQuery.trim()
                      ? searchMatches.length
                        ? `${(matchCursor % searchMatches.length) + 1} of ${searchMatches.length}`
                        : 'No matches'
                      : ''}
                  </span>
                  <button
                    type="button"
                    className="cad-icon-btn"
                    disabled={searchMatches.length === 0}
                    onClick={() => setMatchCursor((c) => (c - 1 + searchMatches.length) % searchMatches.length)}
                    title="Previous match"
                    aria-label="Previous match"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="cad-icon-btn"
                    disabled={searchMatches.length === 0}
                    onClick={() => setMatchCursor((c) => (c + 1) % searchMatches.length)}
                    title="Next match"
                    aria-label="Next match"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="cad-icon-btn"
                    onClick={() => setSearchOpen(false)}
                    title="Close search"
                    aria-label="Close search"
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* The thread endpoint returns at most 200 messages, so search is exact for what is
                  loaded and blind to anything older. Saying so beats returning "no matches" for a
                  message the customer can see in their own WhatsApp. */}
              {searchOpen && (thread.data.messages.length >= 200) && (
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-2xs)' }}>
                  Searching the most recent 200 messages in this conversation.
                </span>
              )}

              <div className="cad-thread-scroll">
              <div className="stack gap-2 message-list" ref={listRef}>
                {/* Assignment/resolution events interleaved by time with the messages themselves,
                    the way WhatsApp renders its own "X added Y" / "call ended" system notices
                    inline rather than in a separate log a teammate has to go looking for. */}
                {(() => {
                  const entries = mergeTimelineEntries(thread.data.messages, thread.data.events ?? [])
                  // Index of the first message that was unread when this thread was opened. The
                  // rule goes above it, so everything below is what arrived while nobody looked.
                  const messageIdxs = entries
                    .map((e, i) => (e.kind === 'message' ? i : -1))
                    .filter((i) => i >= 0)
                  const unreadFromIdx =
                    unreadAnchor > 0 && unreadAnchor <= messageIdxs.length
                      ? messageIdxs[messageIdxs.length - unreadAnchor]
                      : -1
                  // Walked in order so each row can see the one before it: a day change forces a
                  // separator, and anything other than the same sender continuing within a few
                  // minutes forces the name line back on.
                  let prev: { senderKey: string; at: number; dayKey: string } | null = null
                  let prevDay = ''

                  return entries.map((entry, idx) => {
                    if (entry.kind !== 'message') {
                      prev = null // a system notice breaks the run
                      return (
                        <SystemNotice key={entry.event.id} event={entry.event} members={team.data?.members ?? []} />
                      )
                    }

                    const m = entry.message
                    const subject = {
                      senderKey: m.isPrivateNote
                        ? 'note'
                        : `${m.direction}:${m.senderName ?? m.senderPhone ?? ''}`,
                      at: m.sentAt ? new Date(m.sentAt).getTime() : 0,
                      dayKey: m.sentAt ? dayKeyOf(m.sentAt) : '',
                    }
                    const newDay = subject.dayKey !== '' && subject.dayKey !== prevDay
                    const showSender = shouldShowSender(prev, subject)

                    prevDay = subject.dayKey || prevDay
                    prev = subject

                    const bubble = (
                      <MessageBubble
                        key={m.id}
                        message={m}
                        conversationId={selected}
                        conversations={data?.conversations ?? []}
                        tickets={thread.data?.tickets ?? []}
                        members={team.data?.members ?? []}
                        onForwarded={() => void queryClient.invalidateQueries({ queryKey: ['inbox'] })}
                        showSender={showSender}
                        searchState={
                          activeMatchId === m.id ? 'active' : searchMatches.includes(m.id) ? 'hit' : null
                        }
                      />
                    )

                    const needsUnreadRule = idx === unreadFromIdx
                    const needsDay = newDay && m.sentAt

                    return needsUnreadRule || needsDay ? (
                      <Fragment key={`lead-${m.id}`}>
                        {needsDay && m.sentAt && <DaySeparator iso={m.sentAt} />}
                        {needsUnreadRule && (
                          <div className="cad-unread-rule" role="separator" aria-label="Unread messages below">
                            <span>{unreadAnchor === 1 ? '1 unread message' : `${unreadAnchor} unread messages`}</span>
                          </div>
                        )}
                        {bubble}
                      </Fragment>
                    ) : (
                      bubble
                    )
                  })
                })()}
              </div>

              {/* Only while the newest message is off-screen. Doubles as the "you missed
                  something" signal: new mail arriving while an agent reads back through history
                  must not scroll the page under them, so it announces itself here instead. */}
              {!atBottom && (
                <button
                  type="button"
                  className="cad-jump-newest"
                  data-unseen={missedWhileAway}
                  onClick={() => scrollToBottom(true)}
                  aria-label={missedWhileAway ? 'New messages — jump to newest' : 'Jump to newest message'}
                  title={missedWhileAway ? 'New messages — jump to newest' : 'Jump to newest message'}
                >
                  {missedWhileAway ? 'New messages' : '↓'}
                </button>
              )}
              </div>

              {/* Surfaced here, not just the top-bar pill and the Numbers screen, because this is
                  where someone is about to type a reply that would otherwise silently go nowhere.
                  Irrelevant in Notes mode — a private note never reaches WhatsApp regardless of
                  number health, so showing it there would warn about a problem that can't apply. */}
              {!isNote && thread.data?.conversation.numberIssue && (
                <p className="cad-note" style={{ borderColor: 'var(--cad-warn)' }}>
                  {thread.data.conversation.numberIssue}
                </p>
              )}

              {/* A tab pair, not a verb button — WhatsApp/Gallabox's own mode-switch convention.
                  Same `isNote` state as before; this only changes how it's set. */}
              <div className="cad-tabs cad-compose-tabs" role="tablist" aria-label="Compose mode">
                <button
                  type="button"
                  role="tab"
                  className="cad-tab"
                  aria-selected={!isNote}
                  onClick={() => setIsNote(false)}
                >
                  Reply
                </button>
                <button
                  type="button"
                  role="tab"
                  className="cad-tab"
                  aria-selected={isNote}
                  onClick={() => setIsNote(true)}
                >
                  Notes
                </button>
              </div>

              <form
                className="stack gap-2"
                onSubmit={(event: FormEvent) => {
                  event.preventDefault()
                  if (canSend) reply.mutate()
                }}
              >
                <div className="cad-field" style={{ position: 'relative' }}>
                  {/* Visually hidden, not removed: the Reply/Notes tab directly above already
                      says which mode this is, so the printed label was repeating it a few pixels
                      later — but the input still needs an accessible name for a screen reader. */}
                  <label htmlFor="reply-box" className="sr-only">
                    {isNote ? 'Private note to your team' : 'Reply'}
                  </label>
                  {/* A textarea, not an input. An <input> cannot hold a newline at all, so a
                      reply longer than one paragraph simply could not be written here — the
                      single most-felt gap against any real chat client. Enter still sends, the
                      way a chat composer should; Shift+Enter is what inserts the line break. */}
                  <textarea
                    id="reply-box"
                    className="cad-input cad-composer"
                    rows={1}
                    value={draft}
                    disabled={isViewer}
                    onChange={(event) => setDraft(event.target.value)}
                    onPaste={onComposerPaste}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        // Leave the IME alone: while composing Japanese/Chinese/Korean text,
                        // Enter commits the candidate and must not fire the send.
                        if (event.nativeEvent.isComposing) return
                        event.preventDefault()
                        if (canSend) reply.mutate()
                      }
                    }}
                    // Grows with the text instead of scrolling a one-line slot, capped so a long
                    // paste cannot push the conversation off the screen.
                    ref={(el) => {
                      if (!el) return
                      el.style.height = 'auto'
                      el.style.height = `${Math.min(el.scrollHeight, 132)}px`
                    }}
                    placeholder={
                      isViewer
                        ? 'Viewers can read this conversation but cannot reply'
                        : isNote
                          ? 'Not sent to the customer'
                          : 'Type your reply, or / for a saved one'
                    }
                  />

                  {mentionSuggestions.length > 0 && (
                    <div className="quick-reply-list">
                      {mentionSuggestions.map((member) => (
                        <button
                          key={member.userId}
                          type="button"
                          className="quick-reply"
                          onClick={() => setDraft(insertMention(draft, member.name))}
                        >
                          <span style={{ fontWeight: 500, fontSize: 'var(--cad-text-md)' }}>{member.name}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {suggestions.length > 0 && (
                    <div className="quick-reply-list">
                      {suggestions.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className="quick-reply"
                          onClick={() => setDraft(item.body)}
                        >
                          <span style={{ fontWeight: 500, fontSize: 'var(--cad-text-md)' }}>
                            <code>{item.shortcut}</code> {item.title}
                          </span>
                          <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                            {item.body}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* ------------------------------------------------------- attachment */}

                {media ? (
                  <div className="row gap-3" style={{ alignItems: 'center' }}>
                    {mediaPreview ? (
                      <img
                        src={mediaPreview}
                        alt=""
                        style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                    ) : (
                      <span className="cad-pill">{media.kind}</span>
                    )}
                    <span style={{ fontWeight: 500 }}>{media.fileName}</span>
                    <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                      {formatBytes(media.size)}
                    </span>
                    <button type="button" className="cad-btn" data-variant="text" onClick={clearAttachment}>
                      Remove
                    </button>
                  </div>
                ) : (
                  upload.isPending && <span className="cad-muted">Uploading…</span>
                )}

                {upload.error && (
                  <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                    {upload.error instanceof Error ? upload.error.message : 'The file could not be uploaded.'}
                  </p>
                )}

                {translateDraft.error && (
                  <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                    {translateDraft.error instanceof ApiError || translateDraft.error instanceof Error
                      ? translateDraft.error.message
                      : 'The draft could not be translated.'}
                  </p>
                )}

                {/* WhatsApp gives a voice note no caption field, so the host sends the words
                    as a second message rather than losing them. Said here so it is not a
                    surprise in the customer's thread. */}
                {media?.kind === 'audio' && draft.trim() && (
                  <p className="cad-note">
                    A voice note carries no caption — your text will arrive as a second message.
                  </p>
                )}

                <input
                  id="reply-attachment"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => onAttach(e.target.files?.[0])}
                />

                {/* Two tiers, not one flat row: what to do with this draft (send, or switch to
                    a private note — a meaningfully different mode, worth a real label) on the
                    left, and the occasional extras (attach, pin, schedule) as icons pushed to
                    the right, so they read as auxiliary rather than competing for the same
                    attention as "Send reply" itself. */}
                <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                  <button
                    type="submit"
                    className="cad-btn"
                    disabled={!canSend || reply.isPending}
                    title={isDemo ? 'Sign up free to send messages' : isViewer ? 'Viewers cannot send messages' : undefined}
                  >
                    {isDemo
                      ? 'Sign up to send'
                      : isViewer
                        ? 'Read-only'
                        : reply.isPending
                          ? 'Saving…'
                          : isNote
                            ? 'Save note'
                            : 'Send reply'}
                  </button>
                  <div className="row gap-1" style={{ marginLeft: 'auto' }}>
                    <label
                      htmlFor="reply-attachment"
                      className="cad-icon-btn"
                      title="Attach a file"
                      aria-label="Attach a file"
                      role="button"
                    >
                      <AttachIcon />
                    </label>
                    {!isNote && (
                      <>
                        <IconButton
                          label="Translate the draft"
                          disabled={!draft.trim() || translateDraft.isPending}
                          onClick={() => {
                            const targetLanguage = prompt('Translate the draft to which language?', 'Spanish')
                            if (targetLanguage) translateDraft.mutate(targetLanguage)
                          }}
                        >
                          <TranslateIcon />
                        </IconButton>
                        <IconButton
                          label={rewriting ? 'Cancel' : 'Rewrite with AI'}
                          disabled={!draft.trim() || rewriteDraft.isPending}
                          onClick={() => setRewriting((v) => !v)}
                        >
                          <RewriteIcon />
                        </IconButton>
                        <IconButton
                          label={sendingProduct ? 'Cancel' : 'Send a product'}
                          onClick={() => setSendingProduct((v) => !v)}
                        >
                          <ProductIcon />
                        </IconButton>
                        <IconButton
                          label={pinning ? 'Cancel pin' : 'Send a location pin'}
                          onClick={() => (pinning ? clearPin() : setPinning(true))}
                        >
                          <PinIcon />
                        </IconButton>
                        <IconButton label="Schedule for later" onClick={() => setScheduling((v) => !v)}>
                          <ClockIcon />
                        </IconButton>
                      </>
                    )}
                  </div>
                </div>

                {sendingProduct && !isNote && (
                  <div className="stack gap-2">
                    {products.isLoading && <span className="cad-muted">Loading catalogue…</span>}
                    {products.data?.products.length === 0 && (
                      <span className="cad-muted">
                        No products yet — add one under Outbound → Catalogue.
                      </span>
                    )}
                    <div className="row gap-2 wrap">
                      {products.data?.products
                        .filter((p) => p.active)
                        .map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className="cad-btn"
                            data-variant="quiet"
                            disabled={sendProduct.isPending}
                            onClick={() => sendProduct.mutate(p)}
                          >
                            {p.name} · {(p.priceMinor / 100).toFixed(2)} {p.currency}
                          </button>
                        ))}
                    </div>
                    {sendProduct.error && (
                      <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                        {sendProduct.error instanceof ApiError || sendProduct.error instanceof Error
                          ? sendProduct.error.message
                          : 'Could not generate a payment link.'}
                      </p>
                    )}
                  </div>
                )}

                {rewriting && !isNote && (
                  <div className="stack gap-2">
                    <div className="row gap-2 wrap">
                      {(Object.entries(REWRITE_STYLE_LABEL) as [RewriteStyle, string][]).map(([style, label]) => (
                        <button
                          key={style}
                          type="button"
                          className="cad-btn"
                          data-variant="quiet"
                          disabled={rewriteDraft.isPending}
                          onClick={() => rewriteDraft.mutate(style)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {rewriteDraft.error && (
                      <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                        {rewriteDraft.error instanceof ApiError || rewriteDraft.error instanceof Error
                          ? rewriteDraft.error.message
                          : 'Could not rewrite the draft.'}
                      </p>
                    )}
                  </div>
                )}

                {pinning && !isNote && (
                  <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                    <input
                      className="cad-input"
                      style={{ width: '8rem' }}
                      value={pinLat}
                      onChange={(e) => setPinLat(e.target.value)}
                      placeholder="Latitude"
                      inputMode="decimal"
                    />
                    <input
                      className="cad-input"
                      style={{ width: '8rem' }}
                      value={pinLng}
                      onChange={(e) => setPinLng(e.target.value)}
                      placeholder="Longitude"
                      inputMode="decimal"
                    />
                    <input
                      className="cad-input"
                      style={{ width: 'auto' }}
                      value={pinName}
                      onChange={(e) => setPinName(e.target.value)}
                      placeholder="Place name (optional)"
                    />
                    {/* A pin and its note are two messages on the wire — WhatsApp's location
                        content has nowhere to put a caption. */}
                    {pin && draft.trim() && (
                      <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                        Your text will arrive as a second message.
                      </span>
                    )}
                  </div>
                )}

                {scheduling && (
                  <div className="stack gap-2">
                    <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                      <input
                        className="cad-input"
                        type="datetime-local"
                        style={{ width: 'auto' }}
                        value={scheduleAt}
                        onChange={(e) => setScheduleAt(e.target.value)}
                      />
                      <select
                        className="cad-input"
                        style={{ width: 'auto' }}
                        value={scheduleRecurrence.recurrence}
                        onChange={(e) =>
                          setScheduleRecurrence({
                            ...scheduleRecurrence,
                            recurrence: e.target.value as RecurrenceState['recurrence'],
                          })
                        }
                      >
                        <option value="">Once</option>
                        <option value="daily">Repeats daily</option>
                        <option value="weekly">Repeats weekly</option>
                        <option value="monthly">Repeats monthly</option>
                      </select>
                      <button
                        type="button"
                        className="cad-btn"
                        disabled={!draft.trim() || !scheduleAt || scheduleMessage.isPending}
                        onClick={() => scheduleMessage.mutate()}
                      >
                        {scheduleMessage.isPending ? 'Scheduling…' : 'Confirm'}
                      </button>
                      {scheduleMessage.isError && (
                        <span className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                          {scheduleMessage.error instanceof ApiError || scheduleMessage.error instanceof Error
                            ? scheduleMessage.error.message
                            : 'Could not schedule that.'}
                        </span>
                      )}
                    </div>
                    <RecurrenceFields state={scheduleRecurrence} onChange={setScheduleRecurrence} idPrefix="sched" />
                  </div>
                )}

                {(scheduledForChat.data?.messages.length ?? 0) > 0 && (
                  <div className="stack gap-1">
                    <span className="cad-eyebrow">Scheduled</span>
                    {scheduledForChat.data!.messages.map((m) => (
                      <div key={m.id} className="row between wrap gap-2" style={{ alignItems: 'center' }}>
                        <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                          {new Date(m.scheduledFor).toLocaleString()}
                          {m.recurrence
                            ? ` · repeats ${describeRecurrence(m.recurrence, m.recurrenceInterval, m.recurrenceEndAt, m.recurrenceEndCount, m.recurrenceWeekdays)}`
                            : ''}{' '}
                          — {m.body}
                        </span>
                        <button
                          type="button"
                          className="cad-btn"
                          data-variant="text"
                          onClick={() => {
                            if (confirm(`Cancel this scheduled message?

It will not be sent, and the text you wrote is discarded.`)) cancelScheduled.mutate(m.id)
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {reply.isError && (
                  <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                    {reply.error instanceof ApiError || reply.error instanceof Error
                      ? reply.error.message
                      : 'Could not send that message.'}
                  </p>
                )}
                {reply.isSuccess && reply.data?.delivered === false && (
                  <p className="cad-note">Saved as a private note. The customer cannot see it.</p>
                )}
              </form>
            </div>
          ) : null}
        </section>

        {/*
          Tabbed rather than stacked. The panel will grow — contact history, AI, integrations —
          and stacking each new section would push the thread itself off the screen.
        */}
        {selected && thread.data && (
          <section className="cad-card cad-side">
            <div className="cad-tabs" role="tablist">
              {(
                [
                  ['properties', 'Details'],
                  ['ticket', 'Ticket'],
                  ['tasks', 'Tasks'],
                  ['contact', 'Contact'],
                ] as [PanelTab, string][]
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  className="cad-tab"
                  aria-selected={panelTab === id}
                  onClick={() => setPanelTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div style={{ paddingTop: '14px' }}>
              <PropertiesPanel
                conversationId={selected}
                ticketId={activeTicket?.id}
                view={panelTab}
                conversation={thread.data.conversation}
                ticket={activeTicket}
                masked={Boolean(thread.data.masked)}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
